import {
  GetExamCourseCommentsDocument,
  useSendExamCourseCommentMutation,
} from '@exam-management/graphql';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { getCommentFormSchema } from '../../schemas';
import type { CommentFormContextData } from '../../types';

import type { QuestionCommentFormProviderProps } from './types';

export default function QuestionCommentFormProvider(
  props: Readonly<QuestionCommentFormProviderProps>,
) {
  const { children, taskId, entityId, entityType } = props;
  const input = {
    entityId: entityId,
    entityType: entityType,
    taskId: taskId,
  };

  const location = useLocation();

  const CommentFormSchema = getCommentFormSchema();

  const formContext = useForm<CommentFormContextData>({
    defaultValues: {
      comment: '',
    },
    resolver: zodResolver(CommentFormSchema),
  });

  const [comment] = useSendExamCourseCommentMutation({
    refetchQueries: [
      {
        query: GetExamCourseCommentsDocument,
        variables: {
          getExamCourseCommentsInput: input,
        },
      },
    ],
  });

  const submitComment = async (data: { comment: string }) => {
    if (!data.comment) {
      return;
    }
    formContext.setValue('comment', '');
    await comment({
      variables: {
        createExamCourseCommentInput: {
          content: parseContentForApi(data.comment),
          ...input,
        },
      },
    });
  };

  useEffect(() => {
    formContext.reset();
  }, [location.search]);

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(submitComment)}>{children}</Form>
    </FormProvider>
  );
}
